import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Alert from '../components/Alert';
import OrderActionButtons from '../components/OrderActionButtons';
import PatientInfoHeader from '../components/PatientInfoHeader';
import { buildPdfFilename } from '../utils/pdfFilename';
import { getOrderOptionEntries, buildOrderOptionSource } from '../utils/orderOptions';

const Order = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [selectedProduct] = useState(location.state?.selectedProduct || null);
    const [currentMeasurement] = useState(location.state?.currentMeasurement || null);
    const [fromOrderAgain] = useState(location.state?.fromOrderAgain || false);
    const [isReadOnly] = useState(location.state?.isReadOnly || false);
    const [isOrdered, setIsOrdered] = useState(location.state?.ordered || currentMeasurement?.ordered || false);
    const [measurementId, setMeasurementId] = useState(
        location.state?.measurementId || currentMeasurement?._id || currentMeasurement?.id || null
    );

    // Helper function to format patient name as "Lastname, Firstname"
    const formatPatientName = (name) => {
        if (!name || name === 'Unknown' || name === 'Patient') return name;
        const parts = name.trim().split(' ');
        if (parts.length === 1) return name; // Single name, return as-is
        const lastname = parts[parts.length - 1]; // Last word is lastname
        const firstname = parts.slice(0, -1).join(' '); // Everything else is firstname
        return `${lastname}, ${firstname}`;
    };

    const toCapitalizedConcatenatedWords = (value = '') => {
        return String(value)
            .trim()
            .split(/\s+/)
            .filter(Boolean)
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join('');
    };

    const buildPatientNameForReference = () => {
        const lastNameRaw = currentMeasurement?.patientLastName || location.state?.patientLastName || '';
        const firstNameRaw = currentMeasurement?.patientFirstName || location.state?.patientFirstName || '';

        if (lastNameRaw || firstNameRaw) {
            const lastNameFormatted = toCapitalizedConcatenatedWords(lastNameRaw || 'Patient');
            const firstNameFormatted = String(firstNameRaw || '').trim();
            return firstNameFormatted ? `${lastNameFormatted},${firstNameFormatted}` : lastNameFormatted;
        }

        const fallbackName = location.state?.patientName || currentMeasurement?.patientName || 'Patient';
        const commaIndex = String(fallbackName).indexOf(',');

        if (commaIndex >= 0) {
            const fallbackLastName = String(fallbackName).slice(0, commaIndex).trim();
            const fallbackFirstName = String(fallbackName).slice(commaIndex + 1).trim();
            const lastNameFormatted = toCapitalizedConcatenatedWords(fallbackLastName || 'Patient');
            return fallbackFirstName ? `${lastNameFormatted},${fallbackFirstName}` : lastNameFormatted;
        }

        const parts = String(fallbackName).trim().split(/\s+/).filter(Boolean);
        if (parts.length === 0) return 'Patient';
        if (parts.length === 1) return toCapitalizedConcatenatedWords(parts[0]);

        const lastNameFormatted = toCapitalizedConcatenatedWords(parts[parts.length - 1]);
        const firstNameFormatted = parts.slice(0, -1).join(' ');
        return `${lastNameFormatted},${firstNameFormatted}`;
    };

    const formatTimestampYYYYMMDDHHMM = (date = new Date()) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}${month}${day}${hours}${minutes}`;
    };

    const normalizeRefSegment = (value, fallback = 'UNKNOWN') => {
        if (!value) return fallback;
        const cleaned = String(value)
            .trim()
            .replace(/[^a-zA-Z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
        return cleaned || fallback;
    };

    const generateReferenceNumber = () => {
        const rawPatientName = buildPatientNameForReference();

        const patientNameSegment = normalizeRefSegment(rawPatientName, 'PATIENT');
        const timestampSegment = formatTimestampYYYYMMDDHHMM();

        return `${patientNameSegment}-${timestampSegment}`;
    };

    const [organization, setOrganization] = useState(null);
    const [orderData, setOrderData] = useState({
        orderType: 'order',
        referenceNumber: generateReferenceNumber(),
        numberOfItems: 1,
        unit: currentMeasurement?.measurementType === 'madeToMeasure' ? 'piece' : 'pair',
        accountNumber: '',
        orderNote: '',
    });

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(true);
    const [sendingEmail, setSendingEmail] = useState(false);
    const [isOffline, setIsOffline] = useState(typeof navigator !== 'undefined' ? !navigator.onLine : false);
    const [isApiUnavailable, setIsApiUnavailable] = useState(false);

    const hasExistingOrderData = Boolean(
        currentMeasurement?.order && Object.keys(currentMeasurement.order).length > 0
    );

    const canRenderOfflineOrder = Boolean(
        currentMeasurement && (hasExistingOrderData || currentMeasurement?.ordered || location.state?.ordered)
    );

    useEffect(() => {
        const metaTags = [
            ['Cache-Control', 'no-store, no-cache, must-revalidate, max-age=0'],
            ['Pragma', 'no-cache'],
            ['Expires', '0'],
        ];

        const managedMetaTags = metaTags.map(([httpEquiv, content]) => {
            let meta = document.head.querySelector(`meta[http-equiv="${httpEquiv}"]`);
            const isCreated = !meta;

            if (!meta) {
                meta = document.createElement('meta');
                meta.setAttribute('http-equiv', httpEquiv);
                document.head.appendChild(meta);
            }

            meta.setAttribute('content', content);
            return { meta, isCreated };
        });

        const syncOfflineState = () => {
            setIsOffline(typeof navigator !== 'undefined' ? !navigator.onLine : false);
        };

        syncOfflineState();
        window.addEventListener('online', syncOfflineState);
        window.addEventListener('offline', syncOfflineState);
        window.addEventListener('pageshow', syncOfflineState);

        return () => {
            window.removeEventListener('online', syncOfflineState);
            window.removeEventListener('offline', syncOfflineState);
            window.removeEventListener('pageshow', syncOfflineState);

            managedMetaTags.forEach(({ meta, isCreated }) => {
                if (isCreated && meta.parentNode) {
                    meta.parentNode.removeChild(meta);
                }
            });
        };
    }, []);

    // Fetch organization data
    useEffect(() => {
        const fetchOrganizationData = async () => {
            if (isOffline && canRenderOfflineOrder) {
                setLoading(false);
                setIsApiUnavailable(false);
                return;
            }

            try {
                const response = await axios.get('/api/organizations/my-organization');
                const org = response.data.organization;
                setOrganization(org);
                setIsApiUnavailable(false);

                console.log('Organization data:', org);
                console.log('licenseNumber value:', org.licenseNumber, '(empty?', !org.licenseNumber, ')');
                console.log('accountNumber value:', org.accountNumber, '(empty?', !org.accountNumber, ')');
                console.log('Current measurement:', currentMeasurement);
                console.log('Selected product:', selectedProduct);

                // Only pre-fill if there's no existing order data
                if (!hasExistingOrderData) {
                    const updates = {};

                    // Pre-fill account number from organization default
                    if (org.accountNumber && org.accountNumber.trim() !== '') {
                        console.log('✓ Setting accountNumber from organization default:', org.accountNumber);
                        updates.accountNumber = org.accountNumber;
                    } else {
                        console.log('✗ No organization accountNumber to use');
                    }

                    // Auto-fill account number based on product vendor (overrides default)
                    if (selectedProduct?.vendorName && org.partnerVendors?.length > 0) {
                        console.log('Looking for vendor:', selectedProduct.vendorName);
                        console.log('Available vendors:', org.partnerVendors);

                        const matchingVendor = org.partnerVendors.find(
                            pv => pv.vendorName.toLowerCase() === selectedProduct.vendorName.toLowerCase() && pv.active
                        );

                        if (matchingVendor) {
                            console.log('✓ Found matching vendor:', matchingVendor);
                            if (matchingVendor.accountNumber && matchingVendor.accountNumber.trim() !== '') {
                                console.log('✓ Setting accountNumber from vendor:', matchingVendor.accountNumber);
                                updates.accountNumber = matchingVendor.accountNumber;
                            } else {
                                console.log('✗ Vendor has no accountNumber');
                            }
                        } else {
                            console.log('✗ No matching vendor found');
                        }
                    } else {
                        console.log('✗ No selectedProduct or partnerVendors to check');
                    }

                    console.log('Final updates to apply:', updates);

                    // Apply all updates at once
                    if (Object.keys(updates).length > 0) {
                        setOrderData(prev => ({
                            ...prev,
                            ...updates
                        }));
                        console.log('✓ Updates applied!');
                    } else {
                        console.warn('⚠ No updates to apply - please set licenseNumber and/or partnerVendor accountNumber in organization settings');
                    }
                } else {
                    console.log('Skipping pre-fill - order already exists:', currentMeasurement.order);
                }
            } catch (err) {
                console.error('Error fetching organization:', err);
                if ((!err?.response || err?.message === 'Network Error') && !canRenderOfflineOrder) {
                    setIsApiUnavailable(true);
                }
                if (!canRenderOfflineOrder) {
                    setError(t('errors.loadFailed'));
                }
            } finally {
                setLoading(false);
            }
        };

        fetchOrganizationData();
    }, [selectedProduct, currentMeasurement, hasExistingOrderData, canRenderOfflineOrder, isOffline, t]);

    // Load existing order data if measurement is already ordered
    useEffect(() => {
        if (hasExistingOrderData) {
            // Determine default unit based on measurement type if not set
            const defaultUnit = currentMeasurement.measurementType === 'madeToMeasure' ? 'piece' : 'pair';

            setOrderData({
                orderType: currentMeasurement.order.orderType || 'order',
                referenceNumber: currentMeasurement.order.referenceNumber || '',
                numberOfItems: currentMeasurement.order.numberOfItems || 1,
                unit: currentMeasurement.order.unit || defaultUnit,
                accountNumber: currentMeasurement.order.accountNumber || '',
                orderNote: currentMeasurement.order.orderNote || '',
            });
        }
    }, [currentMeasurement, hasExistingOrderData]);

    const resolvedMeasurementId = measurementId || currentMeasurement?._id || currentMeasurement?.id || location.state?.measurementId;

    const sendOrderEmail = async (idToUse, { silent = false } = {}) => {
        if (!idToUse) {
            if (!silent) {
                setError('Cannot send email: missing measurement ID.');
            }
            return false;
        }

        try {
            setSendingEmail(true);
            await axios.post(`/api/measurements/${idToUse}/send-order-email`);

            if (!silent) {
                setSuccess('Order email sent successfully!');
                setTimeout(() => setSuccess(''), 5000);
            }

            return true;
        } catch (emailError) {
            if (silent) {
                console.warn('Failed to send order confirmation email, but order was saved:', emailError);
            } else {
                console.error('Error sending order email:', emailError);
                setError(t('measurements.stockingSelection.saveError') || 'Failed to send order email. Please try again.');
            }
            return false;
        } finally {
            setSendingEmail(false);
        }
    };

    const handleResendOrderEmail = async () => {
        await sendOrderEmail(resolvedMeasurementId);
    };

    const handleOrderSubmit = async (e) => {
        e.preventDefault();

        try {
            setLoading(true);
            let measurementId = location.state?.measurementId;

            // If no measurementId exists, create a new measurement first
            if (!measurementId) {
                const sourceMeasurement = location.state?.currentMeasurement || currentMeasurement || {};
                const patientId = location.state?.patientId || sourceMeasurement.patientId;
                const patientFirstName = location.state?.patientFirstName || sourceMeasurement.patientFirstName || '';
                const patientLastName = location.state?.patientLastName || sourceMeasurement.patientLastName || '';

                if (!patientId) {
                    setError('Missing patient information. Please go back and complete the patient info and measurements.');
                    setTimeout(() => navigate('/patient-info'), 2000);
                    return;
                }

                // Get patient details from sessionStorage if available
                const storedPatientInfo = sessionStorage.getItem('patientInfo');
                let patientDetails = {
                    dateOfBirth: '',
                    gender: '',
                    address: '',
                    phoneNumber: '',
                    email: '',
                    notes: '',
                };

                if (storedPatientInfo) {
                    const patientData = JSON.parse(storedPatientInfo);
                    patientDetails = {
                        dateOfBirth: patientData.dateOfBirth || '',
                        gender: patientData.gender || '',
                        address: patientData.address || '',
                        phoneNumber: patientData.phoneNumber || '',
                        email: patientData.email || '',
                        notes: patientData.notes || '',
                    };
                }

                // Create a new measurement
                const measurementData = {
                    userId: user?._id,
                    organizationId: user?.organization,
                    patientId,
                    patientFirstName,
                    patientLastName,
                    patientDateOfBirth: location.state?.patientDateOfBirth || sourceMeasurement.patientDateOfBirth || patientDetails.dateOfBirth,
                    patientGender: location.state?.patientGender || sourceMeasurement.patientGender || patientDetails.gender,
                    patientAddress: location.state?.patientAddress || sourceMeasurement.patientAddress || patientDetails.address,
                    patientPhoneNumber: location.state?.patientPhoneNumber || sourceMeasurement.patientPhoneNumber || patientDetails.phoneNumber,
                    patientEmail: location.state?.patientEmail || sourceMeasurement.patientEmail || patientDetails.email,
                    notes: location.state?.notes || sourceMeasurement.notes || patientDetails.notes,
                    measurementType: location.state?.measurementType || sourceMeasurement.measurementType || 'offTheShelf',
                    measurements: location.state?.measurements || sourceMeasurement.measurements || {},
                    measurements2: location.state?.measurements2 || sourceMeasurement.measurements2 || {},
                    compressionClass: location.state?.compressionClass || sourceMeasurement.compressionClass,
                    lengthType: location.state?.lengthType || sourceMeasurement.lengthType,
                    knitting: location.state?.knitting || sourceMeasurement.knitting,
                    prescribingDoctor: location.state?.prescribingDoctor || sourceMeasurement.prescribingDoctor || '',
                    selectedProduct: location.state?.selectedProduct || sourceMeasurement.selectedProduct || selectedProduct || {},
                    selections: location.state?.selections || sourceMeasurement.selections || {},
                    matchingProducts: sourceMeasurement.matchingProducts || [],
                    completedSteps: ['patientInfo', 'measurements', 'productSelection'],
                    ordered: false,
                    orderedDate: null,
                    closed: false,
                };

                const createResponse = await axios.post('/api/measurements', measurementData);
                measurementId = createResponse.data._id;
            }

            // Auto-generate reference number if not provided
            let finalReferenceNumber = orderData.referenceNumber;
            if (!finalReferenceNumber || finalReferenceNumber.trim() === '') {
                finalReferenceNumber = generateReferenceNumber();

                // Update local state with auto-generated reference number
                setOrderData(prev => ({
                    ...prev,
                    referenceNumber: finalReferenceNumber
                }));
            }

            // Preserve existing order fields (e.g., orderNote) when saving order details
            let existingOrder = currentMeasurement?.order || {};
            if (measurementId) {
                try {
                    const latestMeasurementResponse = await axios.get(`/api/measurements/${measurementId}`);
                    existingOrder = latestMeasurementResponse.data?.order || existingOrder;
                } catch (fetchError) {
                    console.warn('Could not fetch latest order before save; using local order state.', fetchError);
                }
            }

            // Prepare order data with orderedDate and reference number
            const orderToSave = {
                ...existingOrder,
                ...orderData,
                referenceNumber: finalReferenceNumber,
                orderedDate: new Date(),
            };

            // Update the measurement with order data
            await axios.put(`/api/measurements/${measurementId}`, {
                order: orderToSave,
                ordered: true,
                orderedDate: new Date(),
                completedSteps: ['patientInfo', 'measurements', 'productSelection', 'order'],
            });

            setMeasurementId(measurementId);

            // Send order confirmation email
            await sendOrderEmail(measurementId, { silent: true });

            // Update local state to reflect order completion
            setIsOrdered(true);

            setSuccess('Order saved successfully!');
            // Clear success message after 5 seconds
            setTimeout(() => setSuccess(''), 5000);
        } catch (error) {
            console.error('Error saving order:', error);
            setError(t('measurements.stockingSelection.saveError'));
        } finally {
            setLoading(false);
        }
    };

    const handleNavigateBack = () => {
        navigate('/stocking-selection', {
            state: {
                ...location.state,
                isReadOnly
            }
        });
    };

    const handleDownloadPdf = async () => {
        try {
            // Fetch the HTML template
            const response = await fetch('/measurementsTemplate.html');
            if (!response.ok) {
                throw new Error('Failed to load template');
            }
            const htmlText = await response.text();

            // Parse the HTML
            const parser = new DOMParser();
            const doc = parser.parseFromString(htmlText, 'text/html');

            // Translate all elements with data-i18n attributes
            doc.querySelectorAll('[data-i18n]').forEach(el => {
                const key = el.getAttribute('data-i18n');
                el.textContent = t(key);
            });

            // Helper function to set input values
            const setInput = (id, value) => {
                const el = doc.getElementById(id);
                if (el && value != null && value !== undefined && value !== '') {
                    el.setAttribute('value', String(value));
                }
            };

            // Helper function to set textarea values
            const setTextarea = (id, value) => {
                const el = doc.getElementById(id);
                if (el && value != null && value !== '') {
                    el.textContent = String(value);
                }
            };

            // Re-fetch the measurement so the PDF reflects the latest saved order (e.g. orderNote) instead of stale local state
            let latestMeasurement = null;
            if (resolvedMeasurementId) {
                try {
                    const latestResponse = await axios.get(`/api/measurements/${resolvedMeasurementId}`);
                    latestMeasurement = latestResponse.data;
                } catch (fetchError) {
                    console.warn('Could not fetch latest measurement before generating PDF; using local state.', fetchError);
                }
            }

            // Determine measurement data source (latest fetch, then currentMeasurement, then location.state)
            const measurement = latestMeasurement || currentMeasurement || {
                patientId: location.state?.patientId || '',
                patientName: location.state?.patientName || '',
                patientDateOfBirth: location.state?.patientDateOfBirth || '',
                patientGender: location.state?.patientGender || '',
                patientAddress: location.state?.patientAddress || '',
                patientPhoneNumber: location.state?.patientPhoneNumber || '',
                patientEmail: location.state?.patientEmail || '',
                notes: location.state?.notes || '',
                measurements: location.state?.measurements || {},
                measurements2: location.state?.measurements2 || {},
                compressionClass: location.state?.compressionClass || '',
                lengthType: location.state?.lengthType || '',
                knitting: location.state?.knitting || '',
                prescribingDoctor: location.state?.prescribingDoctor || '',
                measurementType: location.state?.measurementType || '',
                createdAt: location.state?.createdAt || '',
                order: location.state?.order || {},
            };

            // Prepend patient name to the h1 title
            const titleEl = doc.querySelector('h1[data-i18n="pdfTemplate.title"]');
            if (titleEl && measurement.patientName) {
                titleEl.textContent = `${formatPatientName(measurement.patientName)} - ${titleEl.textContent}`;
            }

            // Hide the Patient section in the PDF
            const sections = doc.querySelectorAll('.section');
            // The Patient section is now the first section (index 0)
            if (sections.length >= 1) {
                const patientSection = sections[0];
                if (patientSection) {
                    patientSection.style.display = 'none';
                }
            }

            // Populate patient information
            setInput('patientName', formatPatientName(measurement.patientName) || '');
            setInput('ssid', measurement.patientId || '');
            setInput('address', measurement.patientAddress || '');

            const measurementDate = measurement.createdAt ? (() => {
                const date = new Date(measurement.createdAt);
                const day = String(date.getDate()).padStart(2, '0');
                const month = date.toLocaleString('en-US', { month: 'short' });
                const year = date.getFullYear();
                return `${day}-${month}-${year}`;
            })() : '';
            setInput('measurementDate', measurementDate);

            // Measurement Type - translate the value if available
            if (measurement.measurementType) {
                const translatedMeasurementType = t(`measurements.legMeasuring.measurementType.${measurement.measurementType}`);
                setInput('measurementType', translatedMeasurementType);
            }

            // Prescription details
            setInput('compressionClass', measurement.compressionClass || '');
            setInput('lengthType', measurement.lengthType || '');
            setInput('knitting', measurement.knitting || '');
            setInput('prescribingDoctor', measurement.prescribingDoctor || '');

            // Measurements - using flat structure from database
            const m = measurement.measurements || {};

            // Hip / waist circumferences
            setInput('cT-hm', m.cT || '');
            setInput('cH-hm', m.cH || '');

            // Left leg circumferences
            setInput('left-cG-hm', m.cGL || '');
            setInput('left-cF-hm', m.cFL || '');
            setInput('left-cE-hm', m.cEL || '');
            setInput('left-cD-hm', m.cDL || '');
            setInput('left-cC-hm', m.cCL || '');
            setInput('left-cB1-hm', m.cB1L || '');
            setInput('left-cB-hm', m.cBL || '');
            setInput('left-cY-hm', m.cYL || '');
            setInput('left-cA-hm', m.cAL || '');

            // Right leg circumferences
            setInput('right-cG-hm', m.cGR || '');
            setInput('right-cF-hm', m.cFR || '');
            setInput('right-cE-hm', m.cER || '');
            setInput('right-cD-hm', m.cDR || '');
            setInput('right-cC-hm', m.cCR || '');
            setInput('right-cB1-hm', m.cB1R || '');
            setInput('right-cB-hm', m.cBR || '');
            setInput('right-cY-hm', m.cYR || '');
            setInput('right-cA-hm', m.cAR || '');

            // Length measurements
            setInput('lT-hm', m.lT || '');
            setInput('lH-hm', m.lH || '');
            setInput('lG-hm', m.lG || '');
            setInput('lF-hm', m.lF || '');
            setInput('lE-hm', m.lE || '');
            setInput('lD-hm', m.lD || '');
            setInput('lC-hm', m.lC || '');
            setInput('lB1-hm', m.lB1 || '');
            setInput('lB-hm', m.lB || '');
            setInput('lA-hm', m.lA || '');
            setInput('lZ-hm', m.lZ || '');

            // Check if measurements2 exists and is not empty
            const m2 = measurement.measurements2 || {};
            const hasMeasurements2 = m2 && Object.keys(m2).length > 0;

            if (hasMeasurements2) {
                // Populate ZM measurements from measurements2
                // Hip / waist
                setInput('cT-zm', m2.cT);
                setInput('cH-zm', m2.cH);

                // Left leg circumference
                setInput('left-cG-zm', m2.cGL);
                setInput('left-cF-zm', m2.cFL);
                setInput('left-cE-zm', m2.cEL);
                setInput('left-cD-zm', m2.cDL);
                setInput('left-cC-zm', m2.cCL);
                setInput('left-cB1-zm', m2.cB1L);
                setInput('left-cB-zm', m2.cBL);
                setInput('left-cY-zm', m2.cYL);
                setInput('left-cA-zm', m2.cAL);

                // Right leg circumference
                setInput('right-cG-zm', m2.cGR);
                setInput('right-cF-zm', m2.cFR);
                setInput('right-cE-zm', m2.cER);
                setInput('right-cD-zm', m2.cDR);
                setInput('right-cC-zm', m2.cCR);
                setInput('right-cB1-zm', m2.cB1R);
                setInput('right-cB-zm', m2.cBR);
                setInput('right-cY-zm', m2.cYR);
                setInput('right-cA-zm', m2.cAR);

                // Right leg length measurements
                setInput('lT-zm', m2.lT);
                setInput('lH-zm', m2.lH);
                setInput('lG-zm', m2.lG);
                setInput('lF-zm', m2.lF);
                setInput('lE-zm', m2.lE);
                setInput('lD-zm', m2.lD);
                setInput('lC-zm', m2.lC);
                setInput('lB1-zm', m2.lB1);
                setInput('lB-zm', m2.lB);
                setInput('lA-zm', m2.lA);
                setInput('lZ-zm', m2.lZ);
            } else {
                // Hide all ZM measurements if measurements2 doesn't exist or is empty
                const zmFields = [
                    // Hip/Waist ZM fields
                    'cT-zm', 'cH-zm',
                    // Left leg ZM fields
                    'left-cG-zm', 'left-cF-zm', 'left-cE-zm', 'left-cD-zm', 'left-cC-zm',
                    'left-cB1-zm', 'left-cB-zm', 'left-cY-zm', 'left-cA-zm',
                    // Right leg ZM fields
                    'right-cG-zm', 'right-cF-zm', 'right-cE-zm', 'right-cD-zm', 'right-cC-zm',
                    'right-cB1-zm', 'right-cB-zm', 'right-cY-zm', 'right-cA-zm',
                    // Right leg length measurements
                    'lT-zm', 'lH-zm', 'lG-zm', 'lF-zm', 'lE-zm', 'lD-zm', 'lC-zm',
                    'lB1-zm', 'lB-zm', 'lA-zm', 'lZ-zm'
                ];

                zmFields.forEach(fieldId => {
                    const input = doc.getElementById(fieldId);
                    if (input) {
                        // Hide the parent div that contains both the ZM label and input
                        const parentDiv = input.closest('div');
                        if (parentDiv && parentDiv.querySelector('.measure-type')) {
                            const measureType = parentDiv.querySelector('.measure-type');
                            // Hide if measure type is 'ZM' or 'Right' (for length measurements)
                            if (measureType && (measureType.textContent === 'ZM' || measureType.textContent === 'Right')) {
                                parentDiv.style.display = 'none';
                            }
                        }
                    }
                });

                // Hide "Left" labels for length measurements when there's only one column
                const leftLengthFields = ['lT-hm', 'lH-hm', 'lG-hm', 'lF-hm', 'lE-hm', 'lD-hm', 'lC-hm', 'lB1-hm', 'lB-hm', 'lA-hm', 'lZ-hm'];
                leftLengthFields.forEach(fieldId => {
                    const input = doc.getElementById(fieldId);
                    if (input) {
                        const parentDiv = input.closest('div');
                        if (parentDiv) {
                            const measureTypeSpan = parentDiv.querySelector('.measure-type');
                            if (measureTypeSpan && measureTypeSpan.textContent === 'Left') {
                                measureTypeSpan.style.display = 'none';
                            }
                        }
                    }
                });
            }

            // Hide 'HM' labels when measurementType is 'offTheShelf' (only one set of measurements)
            if (measurement.measurementType === 'offTheShelf') {
                doc.querySelectorAll('.measure-type').forEach(span => {
                    if (span.textContent.trim() === 'HM') {
                        span.style.display = 'none';
                    }
                });
            }

            // Product options from selectedProduct OR measurement.selectedProduct OR measurement.selections
            const sp = selectedProduct || measurement.selectedProduct || {};
            const sel = measurement.selections || {};
            const orderOptionSource = buildOrderOptionSource({
                selections: sel,
                measurement,
                selectedProduct: sp,
            });

            const compressionClass = orderOptionSource.compressionClass || '';
            const lengthType = orderOptionSource.lengthType || '';
            const lengthSize = sel.lengthsize || sel.lengthSize || sp.lengthsize || sp.lengthSize || '';

            // Populate vendor and model information
            setInput('vendorName', sp.vendorName || '');
            setInput('modelName', sp.modelName || '');
            setInput('compressionClass', compressionClass);
            setInput('lengthType', lengthType);
            setInput('lengthSize', lengthSize);
            setInput('vendorType', sel.vendortype || '');
            setInput('size', sel.size || '');

            // Dynamically render all non-empty options from measurement.selections
            const optionsSectionTitle = doc.querySelector('[data-i18n="pdfTemplate.sections.options"]');
            const optionsSection = optionsSectionTitle ? optionsSectionTitle.closest('.section') : null;
            const optionsRow = optionsSection ? optionsSection.querySelector('.form-row') : null;

            const optionEntries = getOrderOptionEntries(orderOptionSource);

            if (optionsSection && optionsRow) {
                optionsRow.innerHTML = '';

                optionEntries.forEach(({ key, value, label: fallbackLabel }) => {
                    const formGroup = doc.createElement('div');
                    formGroup.className = 'form-group';

                    const label = doc.createElement('label');
                    const inputId = `option-${key}`;
                    label.setAttribute('for', inputId);
                    const translated = t(`pdfTemplate.labels.${key}`);
                    label.textContent = translated && translated !== `pdfTemplate.labels.${key}`
                        ? translated
                        : fallbackLabel;

                    const input = doc.createElement('input');
                    input.type = 'text';
                    input.id = inputId;
                    input.setAttribute('value', String(value));

                    formGroup.appendChild(label);
                    formGroup.appendChild(input);
                    optionsRow.appendChild(formGroup);
                });

                if (optionEntries.length === 0) {
                    optionsSection.style.display = 'none';
                }
            }

            // Notes should come from order.orderNote
            setTextarea('notes', measurement.order?.orderNote || '');

            // Populate order information - merge latest saved order with local orderData
            const orderInfo = {
                ...(measurement?.order || {}),
                ...orderData
            };

            // Use existing reference number or display empty for PDF
            const finalReferenceNumber = orderInfo.referenceNumber || '';

            // Order fields
            setInput('referenceNumber', finalReferenceNumber);
            setInput('numberOfItems', orderInfo.numberOfItems || '1');

            // Translate and set unit
            if (orderInfo.unit) {
                const translatedUnit = t(`measurements.stockingSelection.order.${orderInfo.unit}`);
                setInput('unit', translatedUnit);
            }

            setInput('account', orderInfo.accountNumber || '');

            // Set radio buttons for order type (order vs quote)
            const orderTypeRadios = doc.querySelectorAll('input[name="orderType"]');
            orderTypeRadios.forEach(radio => {
                if (radio.value === (orderInfo.orderType || 'order')) {
                    radio.setAttribute('checked', 'checked');
                } else {
                    radio.removeAttribute('checked');
                }
            });

            // Ordered date
            if (measurement?.orderedDate || orderInfo.orderedDate) {
                const orderedDate = new Date(measurement?.orderedDate || orderInfo.orderedDate);
                const day = String(orderedDate.getDate()).padStart(2, '0');
                const month = orderedDate.toLocaleString('en-US', { month: 'short' });
                const year = orderedDate.getFullYear();
                setInput('orderDate', `${day}-${month}-${year}`);
            }

            // Hide the print button in the template (we're auto-printing)
            const printBtn = doc.querySelector('.print-button');
            if (printBtn) {
                printBtn.style.display = 'none';
            }

            // Add auto-print script to the document
            const script = doc.createElement('script');
            script.textContent = `
                window.addEventListener('load', function() {
                    setTimeout(function() {
                        window.print();
                    }, 400);
                });
            `;
            doc.body.appendChild(script);

            doc.title = buildPdfFilename(measurement.patientName, measurement.createdAt);

            // Convert the modified document back to HTML string
            const serializer = new XMLSerializer();
            const populatedHtml = '<!DOCTYPE html>\n' + serializer.serializeToString(doc.documentElement);

            // Create a blob and open it in a new window
            const blob = new Blob([populatedHtml], { type: 'text/html' });
            const url = URL.createObjectURL(blob);
            const printWindow = window.open(url, '_blank');

            if (!printWindow) {
                setError(t('patients.errors.pdfPopupBlocked') || 'Failed to open print window. Please check your popup blocker.');
                URL.revokeObjectURL(url);
                return;
            }

            // Revoke blob URL after enough time for the content to load and print
            setTimeout(() => URL.revokeObjectURL(url), 60000);

        } catch (error) {
            console.error('Error generating PDF:', error);
            setError(t('patients.errors.pdfFailed') || 'Failed to generate PDF. Please try again.');
        }
    };

    if ((isOffline || isApiUnavailable) && !canRenderOfflineOrder) {
        return (
            <div className="page-transition min-h-screen bg-gray-50 flex items-center justify-center px-4 py-8">
                <div className="w-full max-w-xl rounded-2xl border border-yellow-200 bg-white p-8 shadow-lg">
                    <div className="mb-4 flex items-center gap-3 text-yellow-700">
                        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <h1 className="text-xl font-semibold">{t('measurements.stockingSelection.offlineUnavailableTitle') || 'Order details are unavailable offline'}</h1>
                    </div>
                    <p className="text-sm text-gray-700">
                        {t('measurements.stockingSelection.offlineUnavailableText') || 'This page requires a live connection and will not reuse cached data when the browser is offline.'}
                    </p>
                    <div className="mt-6 flex flex-wrap gap-3">
                        <button
                            type="button"
                            onClick={() => navigate('/patients')}
                            className="btn-primary"
                        >
                            {t('common.back')}
                        </button>
                        <button
                            type="button"
                            onClick={() => window.location.reload()}
                            className="btn-secondary"
                        >
                            {t('common.retry') || 'Retry'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!selectedProduct && !currentMeasurement) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="card">
                    <h1 className="text-2xl font-bold text-red-600 mb-4">No Product Selected</h1>
                    <p className="text-gray-600 mb-6">Please select a product before placing an order.</p>
                    <button
                        onClick={() => navigate('/stocking-selection')}
                        className="btn-primary"
                    >
                        {t('common.back')}
                    </button>
                </div>
            </div>
        );
    }

    const productToDisplay = selectedProduct || currentMeasurement?.selectedProduct;
    const patientFirstName = currentMeasurement?.patientFirstName || location.state?.patientFirstName || '';
    const patientLastName = currentMeasurement?.patientLastName || location.state?.patientLastName || '';
    const patientNameFallback = location.state?.patientName || currentMeasurement?.patientName || t('measurements.stockingSelection.unknown');
    const patientId = currentMeasurement?.patientId || location.state?.patientId || t('measurements.stockingSelection.notAvailable');
    const headerLengthType = currentMeasurement?.lengthType || location.state?.lengthType || '';
    const headerCompressionClass = currentMeasurement?.compressionClass || location.state?.compressionClass || '';
    const headerKnitting = currentMeasurement?.knitting || location.state?.knitting || '';
    const headerMeasurementType = currentMeasurement?.measurementType || location.state?.measurementType || '';

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="max-w-4xl mx-auto">
                {/* Progress Indicator - Step 4 */}
                <div className="mb-8">
                    <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center text-green-600 font-semibold">
                            <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center mr-2">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <span>{t('patientInfo.progress.step1')}</span>
                        </div>
                        <div className="flex-1 h-1 bg-green-600 mx-4"></div>
                        <div className="flex items-center text-green-600 font-semibold">
                            <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center mr-2">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <span>{t('patientInfo.progress.step2')}</span>
                        </div>
                        <div className="flex-1 h-1 bg-green-600 mx-4"></div>
                        <div className="flex items-center text-green-600 font-semibold">
                            <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center mr-2">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <span>{t('patientInfo.progress.step3')}</span>
                        </div>
                        <div className={`flex-1 h-1 ${isOrdered ? 'bg-green-600' : 'bg-gray-200'} mx-4`}></div>
                        <div className={`flex items-center ${isReadOnly ? 'text-blue-600' : (isOrdered ? 'text-green-600' : 'text-blue-600')} font-semibold`}>
                            <div className={`w-8 h-8 ${isReadOnly ? 'bg-blue-600' : (isOrdered ? 'bg-green-600' : 'bg-blue-600')} text-white rounded-full flex items-center justify-center mr-2`}>
                                {isReadOnly ? (
                                    '4'
                                ) : isOrdered ? (
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                ) : (
                                    '4'
                                )}
                            </div>
                            <span>{t('patientInfo.progress.step4')}</span>
                        </div>
                    </div>
                </div>

                <div className="flex justify-between items-center mb-4">
                    <h1 className="text-3xl font-bold">{t('measurements.stockingSelection.order.title') || 'Order Details'}</h1>
                    <button
                        onClick={() => navigate('/patients')}
                        className="text-gray-600 hover:text-gray-800"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <Alert type="error" message={error} onDismiss={() => setError('')} />
                <Alert type="success" message={success} onDismiss={() => setSuccess('')} />

                {/* Patient Info Section */}
                <div className="mb-6">
                    <PatientInfoHeader
                        patientFirstName={patientFirstName}
                        patientLastName={patientLastName}
                        patientNameFallback={patientNameFallback}
                        patientId={patientId}
                        lengthType={headerLengthType}
                        compressionClass={headerCompressionClass}
                        knitting={headerKnitting}
                        measurementType={headerMeasurementType}
                    />
                </div>

                {/* Selected Product Summary */}
                {productToDisplay && (
                    <div className="card mb-6">
                        <h2 className="text-xl font-semibold text-blue-900 mb-4">
                            {t('measurements.stockingSelection.order.selectedProduct') || 'Selected Product'}
                        </h2>
                        <div className="bg-blue-50 rounded-lg p-4">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-3 border-b border-blue-200">
                                {productToDisplay.vendorName}
                            </h3>

                            <div className="space-y-2">
                                {productToDisplay.modelName && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Model:</span>
                                        <span className="font-semibold text-gray-900">{productToDisplay.modelName}</span>
                                    </div>
                                )}
                                {productToDisplay.compressionClass && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">{t('measurements.stockingSelection.compression')}:</span>
                                        <span className="font-semibold text-gray-900">Class {productToDisplay.compressionClass}</span>
                                    </div>
                                )}
                                {productToDisplay.lengthType && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">{t('measurements.stockingSelection.lengthType')}:</span>
                                        <span className="font-semibold text-gray-900">{productToDisplay.lengthType}</span>
                                    </div>
                                )}
                                {productToDisplay.knitting && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">{t('measurements.stockingSelection.knitting')}:</span>
                                        <span className="font-semibold text-gray-900">{productToDisplay.knitting}</span>
                                    </div>
                                )}
                                {productToDisplay.size && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">{t('measurements.stockingSelection.size')}:</span>
                                        <span className="font-semibold text-gray-900">{productToDisplay.size}</span>
                                    </div>
                                )}
                                {productToDisplay.lengthsize && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">{t('measurements.stockingSelection.lengthSize')}:</span>
                                        <span className="font-semibold text-gray-900">{productToDisplay.lengthsize}</span>
                                    </div>
                                )}
                                {productToDisplay.toeOption && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">{t('measurements.stockingSelection.toeOption')}:</span>
                                        <span className="font-semibold text-gray-900">{productToDisplay.toeOption}</span>
                                    </div>
                                )}
                                {productToDisplay.footLength && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">{t('measurements.stockingSelection.footLength')}:</span>
                                        <span className="font-semibold text-gray-900">{productToDisplay.footLength}</span>
                                    </div>
                                )}
                                {productToDisplay.bandType && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">{t('measurements.stockingSelection.bandType')}:</span>
                                        <span className="font-semibold text-gray-900">{productToDisplay.bandType}</span>
                                    </div>
                                )}
                                {productToDisplay.bandWidth && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">{t('measurements.stockingSelection.bandWidth')}:</span>
                                        <span className="font-semibold text-gray-900">{productToDisplay.bandWidth}</span>
                                    </div>
                                )}
                                {productToDisplay.ankle && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">{t('measurements.stockingSelection.ankle')}:</span>
                                        <span className="font-semibold text-gray-900">{productToDisplay.ankle}</span>
                                    </div>
                                )}
                                {productToDisplay.color && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">{t('measurements.stockingSelection.filters.color')}:</span>
                                        <span className="font-semibold text-gray-900">{productToDisplay.color}</span>
                                    </div>
                                )}
                                {productToDisplay.design && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">{t('measurements.stockingSelection.design')}:</span>
                                        <span className="font-semibold text-gray-900">{productToDisplay.design}</span>
                                    </div>
                                )}
                                {productToDisplay.pantyShortCompression && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">{t('measurements.stockingSelection.pantyShortCompression')}:</span>
                                        <span className="font-semibold text-gray-900">{productToDisplay.pantyShortCompression}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Order Form or Order Details */}
                <div className="card">
                    {!isOrdered && !currentMeasurement?.ordered ? (
                        <form onSubmit={handleOrderSubmit} className="space-y-4">
                            <h2 className="text-xl font-semibold text-gray-900 mb-4">
                                {t('measurements.stockingSelection.order.enterDetails') || 'Enter Order Details'}
                            </h2>

                            {/* Order Type */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('measurements.stockingSelection.order.orderType') || 'Order / Quote'}
                                </label>
                                <div className="flex space-x-4">
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="orderType"
                                            value="order"
                                            checked={orderData.orderType === 'order'}
                                            onChange={(e) => setOrderData({ ...orderData, orderType: e.target.value })}
                                            className="mr-2"
                                            disabled={isReadOnly}
                                        />
                                        <span>{t('measurements.stockingSelection.order.order') || 'Order'}</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="orderType"
                                            value="quote"
                                            checked={orderData.orderType === 'quote'}
                                            onChange={(e) => setOrderData({ ...orderData, orderType: e.target.value })}
                                            className="mr-2"
                                            disabled={isReadOnly}
                                        />
                                        <span>{t('measurements.stockingSelection.order.quote') || 'Quote'}</span>
                                    </label>
                                </div>
                            </div>

                            {/* Order Details - 3 Column Grid */}
                            <div className="grid grid-cols-3 gap-4">
                                {/* Reference Number */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('measurements.stockingSelection.order.referenceNumber') || 'Reference Number'}
                                    </label>
                                    <input
                                        type="text"
                                        value={orderData.referenceNumber}
                                        onChange={(e) => setOrderData({ ...orderData, referenceNumber: e.target.value })}
                                        className="input-field w-full"
                                        placeholder="PO-2026-0001"
                                        disabled={isReadOnly}
                                    />
                                </div>

                                {/* Number of Items */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('measurements.stockingSelection.order.numberOfItems') || 'Number of Items'}
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={orderData.numberOfItems}
                                        onChange={(e) => setOrderData({ ...orderData, numberOfItems: parseInt(e.target.value) || 1 })}
                                        className="input-field w-full"
                                        disabled={isReadOnly}
                                    />
                                </div>

                                {/* Unit */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('measurements.stockingSelection.order.unit') || 'Unit'}
                                    </label>
                                    <select
                                        value={orderData.unit}
                                        onChange={(e) => setOrderData({ ...orderData, unit: e.target.value })}
                                        className="input-field w-full"
                                        disabled={isReadOnly}
                                    >
                                        <option value="pair">{t('measurements.stockingSelection.order.pair') || 'Pair'}</option>
                                        <option value="piece">{t('measurements.stockingSelection.order.piece') || 'Piece'}</option>
                                    </select>
                                </div>

                                {/* Account Number */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('measurements.stockingSelection.order.accountNumber') || 'Account Number'}
                                    </label>
                                    <input
                                        type="text"
                                        value={orderData.accountNumber}
                                        onChange={(e) => setOrderData({ ...orderData, accountNumber: e.target.value })}
                                        className="input-field w-full"
                                        placeholder="Vendor account number"
                                        disabled={isReadOnly}
                                    />
                                </div>
                            </div>

                            {/* Order note */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('measurements.stockingSelection.order.orderNote') || 'Order Note'}
                                </label>
                                <textarea
                                    value={orderData.orderNote || ''}
                                    onChange={(e) => setOrderData({ ...orderData, orderNote: e.target.value })}
                                    className="input-field w-full min-h-[120px]"
                                    placeholder={t('measurements.stockingSelection.order.orderNotePlaceholder') || 'Add notes for this order'}
                                    disabled={isReadOnly}
                                />
                            </div>

                            {/* Action Buttons */}
                            {!isReadOnly && (
                                <div className="flex justify-between pt-4 border-t">
                                    {fromOrderAgain ? <div /> : (
                                        <button
                                            type="button"
                                            onClick={handleNavigateBack}
                                            className="btn-secondary"
                                        >
                                            {t('common.back')}
                                        </button>
                                    )}
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {loading ? t('common.saving', { defaultValue: 'Saving...' }) : t('measurements.stockingSelection.order.submitOrder', { defaultValue: 'Submit Order' })}
                                    </button>
                                </div>
                            )}

                            {isReadOnly && (
                                <OrderActionButtons
                                    onBack={handleNavigateBack}
                                    onSend={handleResendOrderEmail}
                                    onDownloadPdf={handleDownloadPdf}
                                    onBackToPatients={() => navigate('/patients')}
                                    sendingEmail={sendingEmail}
                                    disableSend={!resolvedMeasurementId}
                                    showBack={!fromOrderAgain}
                                />
                            )}
                        </form>
                    ) : (
                        /* Order Details Display */
                        <div>
                            <h2 className="text-xl font-semibold text-blue-900 mb-6">
                                {t('measurements.stockingSelection.order.orderDetails') || 'Order Details'}
                            </h2>

                            <div className="space-y-3">
                                {/* Order Type */}
                                <div className="bg-blue-50 rounded-lg p-3">
                                    <div className="text-xs text-gray-500 uppercase mb-1">
                                        {t('measurements.stockingSelection.order.orderType') || 'Order Type'}
                                    </div>
                                    <div className="font-semibold text-gray-900">
                                        {orderData.orderType === 'order'
                                            ? (t('measurements.stockingSelection.order.order') || 'Order')
                                            : (t('measurements.stockingSelection.order.quote') || 'Quote')
                                        }
                                    </div>
                                </div>

                                {/* Reference Number */}
                                {orderData.referenceNumber && (
                                    <div className="bg-blue-50 rounded-lg p-3">
                                        <div className="text-xs text-gray-500 uppercase mb-1">
                                            {t('measurements.stockingSelection.order.referenceNumber') || 'Reference Number'}
                                        </div>
                                        <div className="font-semibold text-gray-900 font-mono text-sm">
                                            {orderData.referenceNumber}
                                        </div>
                                    </div>
                                )}

                                {/* Number of Items */}
                                <div className="bg-blue-50 rounded-lg p-3">
                                    <div className="text-xs text-gray-500 uppercase mb-1">
                                        {t('measurements.stockingSelection.order.numberOfItems') || 'Number of Items'}
                                    </div>
                                    <div className="font-semibold text-gray-900">
                                        {orderData.numberOfItems} {orderData.unit ? t(`measurements.stockingSelection.order.${orderData.unit}`) : t('measurements.stockingSelection.order.pair')}
                                    </div>
                                </div>

                                {/* Account Number */}
                                {orderData.accountNumber && (
                                    <div className="bg-blue-50 rounded-lg p-3">
                                        <div className="text-xs text-gray-500 uppercase mb-1">
                                            {t('measurements.stockingSelection.order.accountNumber') || 'Vendor Account Number'}
                                        </div>
                                        <div className="font-semibold text-gray-900 font-mono text-sm">
                                            {orderData.accountNumber}
                                        </div>
                                    </div>
                                )}

                                {/* Order note */}
                                {orderData.orderNote && (
                                    <div className="bg-blue-50 rounded-lg p-3">
                                        <div className="text-xs text-gray-500 uppercase mb-1">
                                            {t('measurements.stockingSelection.order.orderNote') || 'Order Note'}
                                        </div>
                                        <div className="font-semibold text-gray-900 text-sm whitespace-pre-wrap">
                                            {orderData.orderNote}
                                        </div>
                                    </div>
                                )}

                                {/* Ordered Date */}
                                {(currentMeasurement?.order?.orderedDate || currentMeasurement?.orderedDate) && (
                                    <div className="bg-blue-50 rounded-lg p-3">
                                        <div className="text-xs text-gray-500 uppercase mb-1">
                                            {t('measurements.stockingSelection.order.orderedDate') || 'Ordered Date'}
                                        </div>
                                        <div className="font-semibold text-gray-900">
                                            {new Date(currentMeasurement.order?.orderedDate || currentMeasurement.orderedDate).toLocaleDateString()}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Navigation Buttons */}
                            <OrderActionButtons
                                onBack={handleNavigateBack}
                                onSend={handleResendOrderEmail}
                                onDownloadPdf={handleDownloadPdf}
                                onBackToPatients={() => navigate('/patients')}
                                sendingEmail={sendingEmail}
                                disableSend={!resolvedMeasurementId}
                                showBack={!fromOrderAgain}
                                className="flex justify-between pt-6 mt-6 border-t"
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Order;
